import { trpc } from '../lib/trpc.js';
import { formatGbp, formatPct, formatCpl } from '../lib/format.js';
import { clsx } from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';

type BookingStatus = 'PENDING' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

const STATUS_COLOR: Record<BookingStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-red-100 text-red-700',
};

export function PulsePage() {
  const today = trpc.metrics.today.useQuery();
  const pipeline = trpc.metrics.pipeline.useQuery();
  const weekly = trpc.metrics.weeklyMetrics.useQuery({ weeks: 2 });
  const dashboard = trpc.metrics.dashboard.useQuery();

  const isLoading = today.isLoading || pipeline.isLoading || weekly.isLoading || dashboard.isLoading;
  if (isLoading) return <p className="text-gray-500">Loading…</p>;

  const t = today.data;
  const p = pipeline.data;
  const w = weekly.data;
  const d = dashboard.data;
  if (!t || !p || !w || !d) return <p className="text-gray-500">No data</p>;

  const thisWeek = w[w.length - 1]!;
  const lastWeek = w[w.length - 2];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-navy">Pulse</h1>
        <p className="text-sm text-gray-500">
          Live business view · {formatInTimeZone(new Date(), 'Europe/London', 'EEEE, d MMMM')}
        </p>
      </div>

      {/* Today's headline KPIs */}
      <div>
        <h2 className="font-display text-sm uppercase tracking-wider text-gray-500 mb-3">Today</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiTile label="New leads" value={t.today.leads} accent="gold" />
          <KpiTile label="Jobs scheduled" value={t.today.bookingsScheduled} accent="navy" />
          <KpiTile label="Jobs completed" value={t.today.bookingsCompleted} accent="green" />
          <KpiTile label="Ad spend" value={formatGbp(t.today.spend)} accent="red" />
          <KpiTile label="Revenue (completed)" value={formatGbp(t.today.revenue)} accent="green" />
        </div>
      </div>

      {/* Today's jobs — what's on the schedule */}
      {t.todayJobs.length > 0 && (
        <section className="card">
          <h2 className="font-display text-lg text-navy mb-3">Today's calendar</h2>
          <div className="space-y-2">
            {t.todayJobs.map((j) => (
              <div key={j.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0 text-sm">
                <span className="font-mono text-xs text-gray-500 w-12">
                  {j.scheduledAt ? formatInTimeZone(new Date(j.scheduledAt), 'Europe/London', 'HH:mm') : '—'}
                </span>
                <span className={clsx('text-xs px-2 py-0.5 rounded font-medium w-20 text-center', STATUS_COLOR[j.status as BookingStatus])}>
                  {j.status}
                </span>
                <span className="font-medium text-navy flex-1">{j.customerName ?? '(unmatched)'}</span>
                <span className="text-xs text-gray-500 w-20">{j.postcode ?? '—'}</span>
                <span className="text-xs text-gray-500 w-32 truncate">{j.technicianName ?? '—'}</span>
                <span className="font-mono text-xs text-gray-400 w-16 text-right">
                  {j.crewSize ? `${j.crewSize}p` : '—'}
                  {j.durationMin ? ` · ${j.durationMin}m` : ''}
                </span>
                <span className="font-mono text-sm text-navy w-16 text-right">{formatGbp(j.jobValue)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pipeline funnel */}
      <section className="card">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="font-display text-lg text-navy">Sales pipeline</h2>
            <p className="text-xs text-gray-500">
              {p.summary.totalOpen} open deals · £{p.summary.totalOpenValue.toFixed(0)} total ·
              <span className="ml-1 text-navy font-medium">£{p.summary.weightedForecast.toFixed(0)}</span> probability-weighted forecast
            </p>
          </div>
          <div className="text-right text-xs text-gray-500">
            <div>Won (90d): <span className="font-mono text-green-700">{p.summary.wonLast90d.count}</span> · {formatGbp(p.summary.wonLast90d.value)}</div>
            <div>Lost (90d): <span className="font-mono text-red-700">{p.summary.lostLast90d.count}</span> · {formatGbp(p.summary.lostLast90d.value)}</div>
          </div>
        </div>

        {p.stages.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No open deals.</p>
        ) : (
          <div className="space-y-1.5">
            {p.stages.map((s) => {
              const widthPct = p.summary.totalOpen > 0 ? (s.count / p.summary.totalOpen) * 100 : 0;
              return (
                <div key={s.stageId} className="flex items-center gap-3 text-sm">
                  <span className="w-44 truncate text-navy">{s.stageLabel}</span>
                  <div className="flex-1 bg-gray-100 rounded h-7 relative overflow-hidden">
                    <div
                      className="bg-gold h-full rounded"
                      style={{ width: `${widthPct}%` }}
                    />
                    <span className="absolute inset-0 flex items-center px-3 font-mono text-xs text-navy">
                      {s.count} · {s.totalAmount > 0 ? formatGbp(s.totalAmount) : '—'}
                      {s.probability !== null && <span className="text-gray-400 ml-2">@{Math.round(s.probability * 100)}%</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* This week's P&L */}
      <section className="card">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-lg text-navy">This week's P&amp;L</h2>
          <span className="text-xs text-gray-500">
            week of {thisWeek.weekStart}
            {(thisWeek.labor.totalCost ?? 0) === 0 && (
              <span className="ml-2 text-amber-600">· labor £0 — set rates + log hours</span>
            )}
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <PnlRow label="Revenue (this week)"  value={thisWeek.pnl.revenue}    sign="positive" />
          <PnlRow label="− Labor cost"          value={thisWeek.pnl.laborCost}  sign="negative" />
          <PnlRow label="− Ad spend"            value={thisWeek.pnl.adSpend}    sign="negative" />
          <PnlRow label="= Net profit"          value={thisWeek.pnl.netProfit}  sign={thisWeek.pnl.netProfit >= 0 ? 'positive' : 'loss'} highlight />
          <div className="card border-l-4 border-l-navy">
            <p className="text-xs text-gray-500 mb-1">Margin</p>
            <p className={clsx('text-xl font-display', thisWeek.pnl.margin >= 0 ? 'text-navy' : 'text-red-700')}>
              {thisWeek.pnl.revenue > 0 ? `${(thisWeek.pnl.margin * 100).toFixed(1)}%` : '—'}
            </p>
          </div>
        </div>
      </section>

      {/* This week vs last week */}
      <section>
        <h2 className="font-display text-sm uppercase tracking-wider text-gray-500 mb-3">This week vs last</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ComparisonTile label="Leads" curr={thisWeek.leads.created} prev={lastWeek?.leads.created ?? 0} />
          <ComparisonTile label="Bookings" curr={thisWeek.bookings.created} prev={lastWeek?.bookings.created ?? 0} />
          <ComparisonTile label="Completed" curr={thisWeek.bookings.completed} prev={lastWeek?.bookings.completed ?? 0} />
          <ComparisonTile label="Revenue" curr={thisWeek.revenue.realized} prev={lastWeek?.revenue.realized ?? 0} format={formatGbp} />
          <ComparisonTile label="Spend" curr={thisWeek.spend} prev={lastWeek?.spend ?? 0} format={formatGbp} />
          <ComparisonTile label="CPL" curr={thisWeek.rates.cpl} prev={lastWeek?.rates.cpl ?? 0} format={formatCpl} invert />
          <ComparisonTile
            label="Lead → book"
            curr={thisWeek.rates.leadToBookRate}
            prev={lastWeek?.rates.leadToBookRate ?? 0}
            format={formatPct}
          />
          <ComparisonTile
            label="Labor hrs"
            curr={thisWeek.labor.totalLaborHours}
            prev={lastWeek?.labor.totalLaborHours ?? 0}
            format={(v) => `${v.toFixed(1)}h`}
          />
        </div>
      </section>

      {/* Anomalies */}
      {d.anomalies.length > 0 && (
        <section className="card border-amber-200 bg-amber-50/50">
          <h2 className="font-display text-lg text-amber-700 mb-2">⚠ Open anomalies</h2>
          <div className="space-y-1">
            {d.anomalies.map((a) => (
              <div key={a.id} className="text-sm flex justify-between">
                <span>{a.type}: {a.description}</span>
                <span className="text-xs text-gray-500">{formatInTimeZone(new Date(a.detectedAt), 'Europe/London', 'd MMM HH:mm')}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Health footer */}
      <p className="text-xs text-gray-400 text-center">
        HubSpot leads {d.health.lastSyncMinutesAgo !== null ? `${d.health.lastSyncMinutesAgo}m ago` : '—'} ·
        {' '}Zenbooker webhook {d.health.lastWebhookHoursAgo !== null ? `${d.health.lastWebhookHoursAgo}h ago` : 'never'} ·
        {' '}Pipeline cache {p.lastSynced ? formatInTimeZone(new Date(p.lastSynced), 'Europe/London', 'd MMM HH:mm') : 'never'}
      </p>
    </div>
  );
}

function PnlRow({ label, value, sign, highlight }: { label: string; value: number; sign: 'positive' | 'negative' | 'loss'; highlight?: boolean }) {
  const color = sign === 'positive' ? 'text-green-700' : sign === 'loss' ? 'text-red-700' : 'text-gray-700';
  return (
    <div className={clsx('card', highlight && 'border-l-4 border-l-green-500 bg-green-50/30')}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={clsx('text-xl font-display', color)}>{formatGbp(value)}</p>
    </div>
  );
}

function KpiTile({ label, value, accent }: { label: string; value: string | number; accent: 'gold' | 'navy' | 'green' | 'red' }) {
  const accentBorder = {
    gold: 'border-l-gold',
    navy: 'border-l-navy',
    green: 'border-l-green-500',
    red: 'border-l-red-500',
  }[accent];
  return (
    <div className={clsx('card border-l-4', accentBorder)}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-display text-navy">{value}</p>
    </div>
  );
}

function ComparisonTile({
  label, curr, prev, format = (v: number) => String(v), invert = false,
}: {
  label: string;
  curr: number;
  prev: number;
  format?: (v: number) => string;
  invert?: boolean;
}) {
  const delta = prev === 0 ? (curr === 0 ? 0 : 1) : (curr - prev) / prev;
  const sign = delta > 0.005 ? 'up' : delta < -0.005 ? 'down' : 'flat';
  const isGood = invert ? sign === 'down' : sign === 'up';
  return (
    <div className="card">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="flex items-baseline justify-between">
        <p className="text-xl font-display text-navy">{format(curr)}</p>
        {sign !== 'flat' && (
          <span className={clsx('text-xs font-mono', isGood ? 'text-green-600' : 'text-red-600')}>
            {sign === 'up' ? '↑' : '↓'}{(Math.abs(delta) * 100).toFixed(0)}%
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-1 font-mono">prev: {format(prev)}</p>
    </div>
  );
}
